import { useCallback, useMemo, useRef } from "react";
import cx from "classnames";

import { DataTableBodyCellProps as BaseDataTableBodyCellProps } from "../../../base/cells/TableBodyCell";
import { DataTableEditableTextCell } from "../editable-cells/DataTableEditableTextCell";
import { DataTableEditableNumberCell } from "../editable-cells/DataTableEditableNumberCell";
import { DataTableEditableSelectCell } from "../editable-cells/DataTableEditableSelectCell";
import { DataTableEditableSelectAsyncCell } from "../editable-cells/DataTableEditableSelectAsyncCell";
import { DataTableEditableMultiselectCell } from "../editable-cells/DataTableEditableMultiselectCell";
import { DataTableEditableMultiselectAsyncCell } from "../editable-cells/DataTableEditableMultiselectAsyncCell";
import { DataTableEditableBooleanCell } from "../editable-cells/DataTableEditableBooleanCell";

import styles from "./DataTableCells.module.scss";
import { Cell } from "@tanstack/react-table";
import { useCommonPinningStyles } from "../../../internal/getCommonPinningStyles";
import { useDTFocusDispatchContext } from "../context/focus/useDTFocusDispatchContext";
import { EditableCellImperativeHandle } from "../editable-cells/types";
import { CellPosition } from "../../../internal/types";
import { TableRow } from "../../../types";

/**
 * Props for the DataTableBodyMutableCell component
 */
export type DataTableBodyMutableCellProps<T> = Omit<
  BaseDataTableBodyCellProps,
  "onChange"
> & {
  /**
   * The position of the cell in the table
   */
  cellPosition: CellPosition;
  /**
   * The TanStack table cell instance
   */
  cell: Cell<T, unknown>;
  /**
   * Whether the cell is currently hovered
   */
  isHovered: boolean;
};

/**
 * Renders a mutable (editable) body cell in a DataTable. This component handles keyboard navigation
 * and delegates to the appropriate editable cell component (text, select, or multiselect) based on
 * the column's editMode configuration.
 *
 * @param props - The props for the DataTableBodyMutableCell component
 * @param props.cell - The TanStack table cell instance
 * @param props.cellPosition - The position of the cell in the table
 * @param props.children - The content to render in the cell
 * @param props.isHovered - Whether the cell is currently hovered
 * @returns The rendered editable cell component or null if no editMode is configured
 */
export function DataTableBodyMutableCell<T>(
  props: DataTableBodyMutableCellProps<T>,
) {
  const { cell, cellPosition, children, isHovered, ...rest } = props;
  const editableCellRef = useRef<EditableCellImperativeHandle>(null);

  const column = cell.column;

  const rowCanExpand = cell.row.getCanExpand();
  const rowIsExpanded = cell.row.getIsExpanded();
  const toggleRowExpanded = cell.row.toggleExpanded;

  const meta = (cell.row.original as TableRow<T>).meta;
  const cellError = meta?.errors?.[column.id as keyof T];
  const cellWarning = meta?.warnings?.[column.id as keyof T];

  const classes = cx(styles["data-table-body-cell"], rest.className, {
    [styles["data-table-body-cell-hovered"]]: isHovered,
    [styles["data-table-body-cell-error"]]: !!cellError,
    [styles["data-table-body-cell-warning"]]: !cellError && !!cellWarning,
  });
  const commonPinningStyles = useCommonPinningStyles<T>(column);

  const { jumpFocus, moveFocus } = useDTFocusDispatchContext();

  const editMode = column.columnDef.meta?.editMode;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTableCellElement>) => {
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          moveFocus("up");
          return;
        case "ArrowDown":
          event.preventDefault();
          moveFocus("down");
          return;
        case "ArrowLeft":
          event.preventDefault();
          if (rowCanExpand && rowIsExpanded) {
            toggleRowExpanded();
            return;
          }
          moveFocus("left");
          return;
        case "ArrowRight":
          if (rowCanExpand && !rowIsExpanded) {
            toggleRowExpanded();
            return;
          }
          event.preventDefault();
          moveFocus("right");
          return;
        case "Home": {
          event.preventDefault();
          jumpFocus("left");
          return;
        }
        case "End": {
          event.preventDefault();
          jumpFocus("right");
          return;
        }
        case "Enter":
        case "F2":
        case " ": {
          event.preventDefault();
          editableCellRef.current?.invoke();
          return;
        }
        default:
          break;
      }
    },
    [moveFocus, jumpFocus, rowCanExpand, rowIsExpanded, toggleRowExpanded],
  );

  const EditableComponent = useMemo(() => {
    if (editMode === "text") {
      return DataTableEditableTextCell;
    }
    if (editMode === "number") {
      return DataTableEditableNumberCell;
    }
    if (editMode === "select") {
      return DataTableEditableSelectCell;
    }
    if (editMode === "select-async") {
      return DataTableEditableSelectAsyncCell;
    }
    if (editMode === "multiselect") {
      return DataTableEditableMultiselectCell;
    }
    if (editMode === "multiselect-async") {
      return DataTableEditableMultiselectAsyncCell;
    }
    if (editMode === "boolean") {
      return DataTableEditableBooleanCell;
    }
  }, [editMode]);

  if (!EditableComponent) {
    return null;
  }

  return (
    <EditableComponent<T>
      ref={editableCellRef}
      cellPosition={cellPosition}
      cell={cell}
      cellProps={{
        ...rest,
        className: classes,
        onKeyDown: handleKeyDown,
        style: commonPinningStyles,
      }}
      isHovered={isHovered}
    >
      {children}
    </EditableComponent>
  );
}
